import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { useConfig } from './hooks/useConfig.js';
import { DEFAULT_CONFIG } from './config/initialData.js';
import { Settings, Save, RefreshCw, Plus, Trash2, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import Spinner from './Spinner.js';

const AdminSettings = ({ db }) => {
  const { config, loading } = useConfig();
  const [localConfig, setLocalConfig] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('warehouses');

  // Sync local state with hook data once it loads
  useEffect(() => {
    if (!loading && config) {
      setLocalConfig(config);
    }
  }, [loading, config]);

  if (loading || !localConfig) return <Spinner />;

  const handleAddItem = (key) => {
    const newItem = prompt(`Add new item to ${key.replace(/_/g, ' ')}:`);
    if (newItem && newItem.trim()) {
      setLocalConfig(prev => ({
        ...prev,
        [key]: [...prev[key], newItem.trim()]
      }));
    }
  };

  const handleRemoveItem = (key, index) => {
    if (window.confirm("Remove this item?")) {
      const updatedList = localConfig[key].filter((_, i) => i !== index);
      setLocalConfig(prev => ({ ...prev, [key]: updatedList }));
    }
  };

  const handleSaveToFirebase = async () => {
    setIsSaving(true);
    try {
      const configRef = doc(db, "system_config", "metadata");
      await setDoc(configRef, localConfig);
      alert("System Configuration Updated Successfully! All users will see changes instantly.");
    } catch (error) {
      console.error("Error saving config:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm("Reset all settings to original code defaults? This will overwrite Firebase data.")) {
      setLocalConfig(DEFAULT_CONFIG);
    }
  };

  const tabs = [
    { id: 'warehouses', label: 'Warehouses', icon: Settings },
    { id: 'warehouse_staff', label: 'Ops Staff', icon: Settings },
    { id: 'transporters', label: 'Transporters', icon: Settings },
    { id: 'vehicle_types', label: 'Vehicles', icon: Settings },
    { id: 'moc_types', label: 'MOC Types', icon: Settings },
    { id: 'personnel', label: 'Approvers/Personnel', icon: Settings },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Settings size={28} color="#2563eb" /> System Configurations
          </h2>
          <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>
            Modify dropdowns and metadata without changing any code.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={resetToDefaults} style={secondaryBtnStyle}>Reset Defaults</button>
          <button onClick={handleSaveToFirebase} disabled={isSaving} style={saveBtnStyle}>
            {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Push to Firebase
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* SIDEBAR TABS */}
        <div style={{ width: '250px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...tabStyle,
                backgroundColor: activeTab === tab.id ? '#eff6ff' : 'transparent',
                color: activeTab === tab.id ? '#2563eb' : '#64748b',
                fontWeight: activeTab === tab.id ? '700' : '500',
                border: activeTab === tab.id ? '1px solid #bfdbfe' : '1px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* CONTENT AREA */}
        <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
            {activeTab === 'personnel' ? (
                <div>
                    <PersonnelSection title="MOC Prepared By" configKey="moc_prepared_by" items={localConfig.moc_prepared_by} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                    <PersonnelSection title="MOC Verified By" configKey="moc_verified_by" items={localConfig.moc_verified_by} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                    <PersonnelSection title="QC Verified By" configKey="qc_verified_by" items={localConfig.qc_verified_by} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                    <PersonnelSection title="Delivery Confirmed By" configKey="delivery_confirmed_by" items={localConfig.delivery_confirmed_by} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                    <PersonnelSection title="Sales Team" configKey="sales_team" items={localConfig.sales_team} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                    <PersonnelSection title="Finance Approvers" configKey="finance_verified_by" items={localConfig.finance_verified_by} onAdd={handleAddItem} onRemove={handleRemoveItem} />
                </div>
            ) : (
                <ListEditor 
                    title={tabs.find(t => t.id === activeTab).label} 
                    configKey={activeTab} 
                    items={localConfig[activeTab]} 
                    onAdd={handleAddItem} 
                    onRemove={handleRemoveItem} 
                />
            )}
        </div>
      </div>
    </div>
  );
};

const ListEditor = ({ title, configKey, items, onAdd, onRemove }) => (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px' }}>
            <h3 style={{ margin: 0, color: '#1e293b' }}>{title}</h3>
            <button onClick={() => onAdd(configKey)} style={addBtnStyle}><Plus size={16} /> Add New</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {items.map((item, idx) => (
                <div key={idx} style={tagStyle}>
                    {item}
                    <button onClick={() => onRemove(configKey, idx)} style={removeBtnStyle}><Trash2 size={12} /></button>
                </div>
            ))}
        </div>
    </div>
);

const PersonnelSection = ({ title, configKey, items, onAdd, onRemove }) => (
    <div style={{ marginBottom: '30px' }}>
        <ListEditor title={title} configKey={configKey} items={items} onAdd={onAdd} onRemove={onRemove} />
    </div>
);

// --- STYLES ---
const tabStyle = { padding: '12px 15px', borderRadius: '8px', border: 'none', textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px' };
const saveBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const secondaryBtnStyle = { backgroundColor: 'white', color: '#64748b', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' };
const addBtnStyle = { backgroundColor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' };
const tagStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', fontSize: '13px', color: '#334155' };
const removeBtnStyle = { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' };

export default AdminSettings;
